import React, { useState } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import Paper from '@mui/material/Paper';
import Avatar from '@mui/material/Avatar';
import { Tooltip, Switch, Snackbar, Alert } from '@mui/material';
import { Usuario } from '../../services/usuarioService';
import useToggleUsuarioAtivo from '../../hooks/useToggleUsuarioAtivo';

export interface AdminUsuarioTableProps {
  usuarios: Usuario[];
  page: number;
  rowsPerPage: number;
  total: number;
  onPageChange: (page: number, rowsPerPage: number) => void;
  onEdit: (usuario: Usuario) => void;
  onChangePassword: (usuario: Usuario) => void;
}

const AdminUsuarioTable: React.FC<AdminUsuarioTableProps> = ({
  usuarios,
  page,
  rowsPerPage,
  total,
  onPageChange,
  onEdit,
  onChangePassword,
}) => {
  const { toggle: toggleUsuario, isLoading } = useToggleUsuarioAtivo();

  const [optimisticById, setOptimisticById] = useState<Record<string, boolean>>({});
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const handleCloseSnackbar = () => setSnackbar((s) => ({ ...s, open: false }));

  const handleChangePage = (_: unknown, newPage: number) => {
    onPageChange(newPage, rowsPerPage);
  };

  const handleChangeRowsPerPage = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    onPageChange(0, parseInt(e.target.value, 10));
  };

  const getChecked = (userId: string, fallback: boolean) => {
    if (userId in optimisticById) return optimisticById[userId];
    return fallback;
  };

  const onToggleAtivo = async (userId: string, current: boolean) => {
    const target = !current;
    setOptimisticById((prev) => ({ ...prev, [userId]: target }));
    try {
      await toggleUsuario(userId, target);
      setSnackbar({
        open: true,
        message: target ? 'Usuário ativado.' : 'Usuário desativado.',
        severity: 'success',
      });
    } catch {
      setOptimisticById((prev) => {
        const copy = { ...prev };
        delete copy[userId];
        return copy;
      });
      setSnackbar({ open: true, message: 'Falha ao atualizar usuário.', severity: 'error' });
    }
  };

  return (
    <TableContainer component={Paper}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ backgroundColor: '#6c757d' }}>
            <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Imagem</TableCell>
            <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Nome</TableCell>
            <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Email</TableCell>
            <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Ativo</TableCell>
            <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Ações</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {usuarios.map((u) => (
            <TableRow key={u.id} hover>
              <TableCell>
                <Avatar
                  src={u.imagemUrl || '/assets/images/user.png'}
                  alt={u.nome || 'avatar'}
                />
              </TableCell>
              <TableCell>
                {u.nome || '-'}
                {u.username && (
                  <div className="text-primary" style={{ fontSize: '0.8em' }}>
                    @{u.username}
                  </div>
                )}
              </TableCell>
              <TableCell>{u.email || '-'}</TableCell>
              <TableCell>
                <Tooltip title={getChecked(u.id, u.ativo) ? 'Desativar usuário' : 'Ativar usuário'}>
                  <span>
                    <Switch
                      color="primary"
                      checked={getChecked(u.id, u.ativo)}
                      onChange={() => onToggleAtivo(u.id, getChecked(u.id, u.ativo))}
                      disabled={isLoading(u.id)}
                      inputProps={{ 'aria-label': 'Ativar ou desativar usuário' }}
                    />
                  </span>
                </Tooltip>
              </TableCell>
              <TableCell>
                <div className="d-flex gap-2">
                  <Tooltip title="Editar">
                    <button
                      type="button"
                      className="btn btn-link text-primary p-0"
                      onClick={() => onEdit(u)}
                    >
                      <i className="bi bi-pencil" />
                    </button>
                  </Tooltip>
                  <Tooltip title="Alterar Senha">
                    <button
                      type="button"
                      className="btn btn-link text-warning p-0"
                      onClick={() => onChangePassword(u)}
                    >
                      <i className="bi bi-key" />
                    </button>
                  </Tooltip>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25]}
      />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </TableContainer>
  );
};

export default AdminUsuarioTable;
